import type {
  AcknowledgementResponse,
  AuthenticatedUser,
  AuthSessionResponse,
  SessionSummary,
} from '@solar/contracts';

import { apiClient } from '../../lib/api-client';

import { singleFlightRefresh } from './refresh-coordinator';

/**
 * The authentication endpoints.
 *
 * `skipAuthRetry` is set on everything here. These calls either establish a
 * session or report that there is none, so retrying them through the refresh
 * path would either recurse or mask a genuine sign-out.
 */
export const authApi = {
  async register(email: string, password: string): Promise<AcknowledgementResponse> {
    return apiClient.post('/auth/register-customer', { email, password }, { skipAuthRetry: true });
  },

  async login(email: string, password: string): Promise<AuthSessionResponse> {
    return apiClient.post('/auth/login', { email, password }, { skipAuthRetry: true });
  },

  async refresh(): Promise<AuthSessionResponse> {
    return singleFlightRefresh(() =>
      apiClient.post('/auth/refresh', undefined, { skipAuthRetry: true }),
    );
  },

  async logout(): Promise<AcknowledgementResponse> {
    return apiClient.post('/auth/logout', undefined, { skipAuthRetry: true });
  },

  async me(): Promise<AuthenticatedUser> {
    return apiClient.get('/auth/me');
  },

  async verifyEmail(token: string): Promise<AcknowledgementResponse> {
    return apiClient.post('/auth/verify-email', { token }, { skipAuthRetry: true });
  },

  async resendVerification(email: string): Promise<AcknowledgementResponse> {
    return apiClient.post('/auth/resend-verification', { email }, { skipAuthRetry: true });
  },

  async forgotPassword(email: string): Promise<AcknowledgementResponse> {
    return apiClient.post('/auth/forgot-password', { email }, { skipAuthRetry: true });
  },

  async resetPassword(token: string, password: string): Promise<AcknowledgementResponse> {
    return apiClient.post('/auth/reset-password', { token, password }, { skipAuthRetry: true });
  },

  async listOwnSessions(): Promise<SessionSummary[]> {
    return apiClient.get('/auth/sessions');
  },

  async revokeOwnSession(sessionId: string): Promise<{ revoked: true }> {
    return apiClient.delete(`/auth/sessions/${sessionId}`);
  },
};
